import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../../assets';
import { BlurredContainerM2030 } from '../../../components/containers/containers';
import DefaultScreen from '../../../components/containers/default-screen';
import StyledReactSelect from '../../../components/inputs/styled-react-select';
import InfiniteList from '../../../components/lists/infinite-list';
import ListControls from '../../../components/lists/list-controls';
import ListHeader from '../../../components/lists/list-header';
import { Fallback } from '../../../components/utils/error-boundary';
import { formatDateTime } from '../../../helpers/date-formatters';
import { getStatusOptions, getMoneyTransitionOrderingOptions } from '../../../helpers/type-options';
import { GraphqlOperations } from '../../../logic/graphql/operations';
import {
  Account,
  CurrencyListType,
  CurrencyType,
  MoneyTransitionListItem,
  OrderingDirection,
  OrderingType,
  Query,
  Status,
  Subscription,
} from '../../../logic/graphql/types/types.d';
import { Paths } from '../../../navigation/paths';
import ListItem from './components/money-transition-list-item';
import { FormattedMessage, useIntl } from 'react-intl';

const MoneyTransitions = (): JSX.Element => {
  // MARK: - Resources
  const history = useHistory();
  const intl = useIntl();

  const statusOptions = useMemo(() => {
    return getStatusOptions(intl, 'nopartialorexpired');
  }, [intl]);

  const moneyTransitionOrderingOptions = useMemo(() => {
    return getMoneyTransitionOrderingOptions(intl);
  }, [intl]);

  // MARK: - State
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);
  const [selectedOrderingType, setSelectedOrderingType] = useState<OrderingType>(
    OrderingType.OrderDate,
  );
  const [selectedOrderingDirection, setSelectedOrderingDirection] = useState<OrderingDirection>(
    OrderingDirection.Desc,
  );
  const [selectedStatus, setSelectedStatus] = useState<Status | 'all'>(Status.Active);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [items, setItems] = useState<MoneyTransitionListItem[]>([]);

  // MARK: - Queries
  const [sumPortfolioLastUpdateQueryResult, reexecuteSumPortfolioLastUpdateQuery] = useQuery<Query>(
    {
      query: GraphqlOperations.queries.sum_portfolioLastUpdate.queryString,
      requestPolicy: 'cache-and-network',
    },
  );

  const [itemsResultQuery, reexecuteItemsResultQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_money_transitions.queryString,
    variables: GraphqlOperations.queries.get_money_transitions.createVars(
      currentPage,
      10,
      selectedOrderingType,
      selectedOrderingDirection,
      selectedStatus,
      selectedCurrencies,
      selectedAccount,
    ),
    requestPolicy: 'network-only',
    pause: selectedAccount === '',
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
  const fetchNextPage = (startIndex: number, stopIndex: number): any => {
    if (startIndex / 10 >= currentPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const userAccountsQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAccounts.queryString,
  })[0];

  const currenciesQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.get_currencies.queryString,
    variables: GraphqlOperations.queries.get_currencies.createVars(
      CurrencyType.Transition,
      CurrencyListType.OwnHistory,
      selectedAccount,
    ),
    pause: selectedAccount === '',
    requestPolicy: 'network-only',
  })[0];

  // MARK: - Subscriptions

  const handleNewMoneyTransitionSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionMoneyTransitions: MoneyTransitionListItem[] = [],
    response: Subscription,
  ): MoneyTransitionListItem[] => {
    if (response && response.new_money_transition) {
      resetList();
      reexecuteItemsResultQuery();
      reexecuteSumPortfolioLastUpdateQuery();
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.new_money_transition.subscriptionString,
    },
    handleNewMoneyTransitionSubscription,
  );

  const handleUpdateMoneyTransitionSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionMoneyTransitions: MoneyTransitionListItem[] = [],
    response: Subscription,
  ): MoneyTransitionListItem[] => {
    if (response && response.update_money_transition) {
      const item = response.update_money_transition;
      const itemsCopy = [...items];
      const itemToChangeIndex = itemsCopy.findIndex(
        (i) => i.transaction_code === item.transaction_code,
      );
      if (itemToChangeIndex >= 0) {
        itemsCopy[itemToChangeIndex] = item;
      }
      setItems(itemsCopy);
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_money_transition.subscriptionString,
    },
    handleUpdateMoneyTransitionSubscription,
  );

  // MARK: - Lifecycle methods

  useEffect(() => {
    if (
      itemsResultQuery.data?.get_money_transitions?.page_count !== undefined &&
      itemsResultQuery.data.get_money_transitions.page === currentPage
    ) {
      setItems([...items.concat(itemsResultQuery.data.get_money_transitions.money_transitions)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsResultQuery.data]);

  useEffect(() => {
    const accounts = userAccountsQueryResult.data?.getUser?.accounts;
    if (accounts && accounts[0]) {
      setSelectedAccount(accounts[0].number);
    }
  }, [userAccountsQueryResult]);

  // MARK: - Event handlers

  const handleCreateClick = (): void => {
    history.push(Paths.contact);
  };

  // MARK: - Helpers

  const resetList = (): void => {
    setCurrentPage(1);
    setItems([]);
  };

  // MARK: - UI Methods

  const hasZeroItems =
    !itemsResultQuery.fetching &&
    itemsResultQuery.data?.get_money_transitions?.money_transitions &&
    items.length === 0;

  const noContent = (
    <p className="self-center text-12 py-6 text-gray" style={{ height: 'calc(100vh - 240px)' }}>
      <FormattedMessage id="moneyTransitions.noContent" />
    </p>
  );

  const hasNextPage =
    itemsResultQuery.fetching ||
    (itemsResultQuery.data?.get_money_transitions?.page_count !== undefined &&
      itemsResultQuery.data?.get_money_transitions?.page !== undefined &&
      itemsResultQuery.data.get_money_transitions.page_count >
        itemsResultQuery.data.get_money_transitions.page);

  const userAccountOptions =
    userAccountsQueryResult.data?.getUser?.accounts?.map((account: Account) => ({
      value: account.number,
      label: `${account.type}: ${account.number}`,
    })) || [];

  return (
    <DefaultScreen
      background={assets.images.orders_bg}
      title={intl.formatMessage({ id: 'moneyTransitions.title' })}
      updatedAt={formatDateTime(sumPortfolioLastUpdateQueryResult.data?.sum_portfolio?.last_update)}
      rightContent={
        userAccountsQueryResult.data?.getUser ? (
          <StyledReactSelect
            placeholder={intl.formatMessage({ id: 'common.accountSelectPlaceholder' })}
            type="blur"
            bold
            defaultValue={userAccountOptions[0]}
            options={userAccountOptions}
            onChange={(value) => {
              if (value !== selectedAccount) {
                resetList();
                setSelectedAccount(value as string);
              }
            }}
          />
        ) : undefined
      }
    >
      <BlurredContainerM2030 className="flex flex-col">
        <ListHeader
          createButtonLabel={intl.formatMessage({ id: 'moneyTransitions.newInstructionButton' })}
          onCreateClick={handleCreateClick}
        >
          <ListControls
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedOrderingType={selectedOrderingType}
            selectedOrderingDirection={selectedOrderingDirection}
            setSelectedOrderingDirection={setSelectedOrderingDirection}
            setSelectedOrderingType={setSelectedOrderingType}
            setSelectedInstruments={setSelectedCurrencies}
            resetList={resetList}
            statusOptions={statusOptions}
            orderingOptions={moneyTransitionOrderingOptions}
            istrumentPlaceholder={intl.formatMessage({ id: 'common.instrumentSelectPlaceholder' })}
            instrumentOptions={
              currenciesQueryResult.data?.get_currencies.map((c) => ({
                value: c.currency,
                label: c.currency,
              })) || []
            }
          />
        </ListHeader>
        {hasZeroItems ? (
          noContent
        ) : (
          <InfiniteList
            hasNextPage={hasNextPage}
            isNextPageLoading={itemsResultQuery.fetching}
            items={items}
            loadNextPage={fetchNextPage}
            listItem={ListItem}
            desktopItemHeight={84}
            mobileItemHeight={165}
          />
        )}
      </BlurredContainerM2030>
    </DefaultScreen>
  );
};

export default withErrorBoundary(MoneyTransitions, { fallback: Fallback });
